import { Component, h, Host, Prop, State, forceUpdate, Watch, Event, EventEmitter } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";
import Fragment from "../../utils/Fragment";
import { ProductType, getMachineList } from "../../api/machine";
import {
    SOFTWARE_TYPES,
    Software,
    SoftwareType,
    getSoftwareModulesByType,
    getSoftwareTypes,
    getSoftwares
} from "../../api/software";
import {
    NOTIFICATION_TYPES,
    NotificationType,
    NotificationTypeId,
    NotificationTypeName,
    getNotificationTypes
} from "../../api/notificationType";
import { APPLICATION_IDS, ApplicationId, applicationsLaser, applicationsOptics, applicationsWelding } from "../../api/applications";
import { classifications } from "../../api/classifications";
import { SALUTATION, SalutationName } from "../../api/salutation";
import { CreateCaseRequest, createRequestData } from "../../api/createCase";
import { getCountryFromLocale, getLanguageFromLocale } from "../../utils/locale";
import { authConfig } from "../../config/auth";
import { ApiClient } from "../../utils/ApiClient";
import { whenAuthenticated } from "../../utils/whenAuthenticated";

const baseClass = "mf-create-service-call";

/**
 * @slot contacts-button - Slot for native contacts button to select contact data from phone address book
 * @slot connector - Slot for the connector that handles sending the form data to a backend
 */
@Component({
    tag: "mf-create-service-call",
    styleUrl: "create-service-call.css",
    shadow: false
})
export class MfCreateServiceCall {
    /**
     * If true, the form will be displayed in multiple steps
     */
    @Prop() multipleSteps: boolean = false;

    /**
     * Enable file upload in the form
     */
    @Prop() fileUpload: boolean = false;

    /**
     * application name that will be used for identification of the source when sending the form
     */
    // @Prop() applicationName: string = "mf-service-calls";

    /**
     * The locale to use for translations and the phone input
     */
    @Prop({ reflect: true }) locale: string = "en-US";

    /**
     * comma separated list of error numbers that should be prefilled in the form
     * @optional
     * @example "0000FC00,1.10.58102409,6000"
     */
    @Prop() errorNumbers: string = "";

    /**
     * salutation for prefilling the contact form
     * @optional
     */
    @Prop() salutation: SalutationName | null = null;

    /**
     * first name for prefilling the contact form
     * @optional
     */
    @Prop() firstName: string = "";

    /**
     * last name for prefilling the contact form
     * @optional
     */
    @Prop() lastName: string = "";

    /**
     * email for prefilling the contact form
     * @optional
     */
    @Prop() email: string = "";

    /**
     * phone number for prefilling the contact form
     * @optional
     */
    @Prop() phone: string = "";

    /**
     * preselected notification type
     * @optional
     */
    @Prop() notificationTypeName: NotificationTypeName | null = null;

    /**
     * use non-prod endpoints
     * @optional
     */
    @Prop() nonProd: boolean = false;

    /**
     * disable silent auth - then the application needs to supply the auth token via @trumpf/ux.utils
     * @optional
     */
    @Prop() disableSilentAuth: boolean = false;

    /**
     * Silent Auth callback url
     * For most use cases this is not needed.
     * @optional
     */
    @Prop() silentAuthCallbackUrl: string | null = null;

    @State() step: number = 1;
    @State() machinesLoading: boolean = true;
    @State() machines: ProductType[] = [];
    @State() softwaresLoading: boolean = true;
    @State() softwares: Software[] = [];
    @State() notificationTypesLoading: boolean = true;
    @State() notificationTypes: NotificationType[] = [];

    @State() selectedNotificationType: NotificationTypeId | null = null;
    @State() selectedSoftwareType: SoftwareType | null = null;
    @State() selectedApplication: ApplicationId | null = null;
    @State() formIsSubmitted: boolean = false;
    @State() selectedMachineTechnology: string | null = null;

    private apiClient: ApiClient;
    private i18nSubscriberId: string;
    private form: HTMLFormElement;
    private callId: string = crypto.randomUUID(); // this browser API only works on localhost and over https
    private unsubscribeAuthenticated: () => void;

    constructor() {
        // TODO: refactor this so it works similar to proactive-case-closure with support for legacyApi
        this.apiClient = new ApiClient(this.nonProd ? "non-prod" : "prod");
    }

    connectedCallback() {
        this.loadTranslations();
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.unsubscribeAuthenticated = whenAuthenticated(() => this.loadFormData());
    }

    disconnectedCallback() {
        this.unsubscribeAuthenticated();
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    private loadFormData() {
        this.apiClient.requestTbdbToken().then(() => {
            this.loadMachines();
            this.loadSoftwares();
            this.loadNotificationTypes();
        });
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    @Watch("notificationTypeName")
    onNotificationTypeNameChange() {
        if (this.notificationTypeName) {
            this.selectedNotificationType = NOTIFICATION_TYPES[this.notificationTypeName];
        }
    }

    /**
     * Event that is fired when the form is submitted
     */
    @Event({
        eventName: "mfServiceCallSubmit",
        bubbles: true,
        composed: true
    })
    serviceCallSubmit: EventEmitter<CreateCaseRequest>;

    /**
     * Event that is fired when the form is cancelled and the form should be closed
     */
    @Event({
        eventName: "mfServiceCallCancel",
        bubbles: true,
        composed: true
    })
    serviceCallCancel: EventEmitter<void>;

    private async loadMachines() {
        this.machinesLoading = true;
        this.machines = await getMachineList(this.apiClient);
        this.machinesLoading = false;
    }

    private async loadSoftwares() {
        this.softwaresLoading = true;
        this.softwares = await getSoftwares(this.apiClient);
        this.softwaresLoading = false;
    }

    private async loadNotificationTypes() {
        this.notificationTypesLoading = true;
        this.notificationTypes = await getNotificationTypes(this.apiClient);
        this.notificationTypesLoading = false;
        if (this.notificationTypeName) {
            this.selectedNotificationType = NOTIFICATION_TYPES[this.notificationTypeName];
        }
    }

    private loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }

    private onSubmit(event: Event) {
        event.preventDefault();
        const data = createRequestData({
            form: this.form,
            callId: this.callId,
            // createdWith: this.applicationName,
            softwares: this.softwares,
            machines: this.machines
        });
        this.serviceCallSubmit.emit(data);
        this.formIsSubmitted = true;
    }

    private onCancel() {
        this.serviceCallCancel.emit();
    }

    render() {
        const t = (key: TranslationKey, options: unknown = undefined) =>
            i18nProvider.t(`mf-service-calls:${String(key)}`, options as Record<string, unknown>);
        const loading = this.machinesLoading || this.softwaresLoading || this.notificationTypesLoading;
        const softwareTypes = getSoftwareTypes(this.softwares);
        const showErrorNumbers =
            this.selectedNotificationType === null ||
            this.selectedNotificationType === NOTIFICATION_TYPES.FAULT_WITHOUT_STOP ||
            this.selectedNotificationType === NOTIFICATION_TYPES.MACHINE_STOP || // TODO: this should be in relation to the selected machine type
            this.selectedNotificationType === NOTIFICATION_TYPES.APPLICATION_ISSUES;
        const authSettings = this.nonProd ? authConfig.qa : authConfig.prod;
        const redirectUri = this.silentAuthCallbackUrl !== null ? this.silentAuthCallbackUrl : authSettings.redirectUri;

        return (
            <Host>
                {!this.formIsSubmitted && (
                    <ux-form data-validation-required--msg={t("validation.requiredField")}>
                        <form onSubmit={(e) => this.onSubmit(e)} ref={(el) => (this.form = el as HTMLFormElement)}>
                            <div class="ux-u-text-align--center ux-u-margin-top-60 ux-u-margin-bottom-60">
                                <div class="ux-text-headline ux-text-headline--big">{t("headline.createCase")}</div>
                            </div>

                            {loading && <ux-loader variant={"spinner"}></ux-loader>}
                            {this.multipleSteps && (
                                <div class="ux-u-margin-top-40 ux-u-margin-bottom-40">
                                    <ux-steps>
                                        <ux-steps-item
                                            onClick={() => (this.step = 1)}
                                            number={1}
                                            current={this.step === 1}
                                            done={this.step > 1}
                                        >
                                            {t("headline.caseDetails")}
                                        </ux-steps-item>
                                        <ux-steps-item onClick={() => (this.step = 2)} number={2} current={this.step === 2}>
                                            {t("headline.contactData")}
                                        </ux-steps-item>
                                    </ux-steps>
                                </div>
                            )}
                            {((this.multipleSteps && this.step === 1) || !this.multipleSteps) && (
                                <div class="ux-row ux-u-flex-jc-center">
                                    <div class="ux-col-12 ux-col-md-10">
                                        <div class="ux-row">
                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-select
                                                        required
                                                        label={t("field.reason.label")}
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        name="notificationType"
                                                        onUxChange={(event) => {
                                                            this.selectedNotificationType = Number(event.detail.value);
                                                        }}
                                                    >
                                                        <option value="" selected disabled>
                                                            {t("select.placeholder")}
                                                        </option>
                                                        {this.notificationTypes.map((notificationType) => (
                                                            <option
                                                                key={String(notificationType.LabelKey)}
                                                                value={notificationType.Id}
                                                                selected={notificationType.Id === this.selectedNotificationType}
                                                            >
                                                                {t(
                                                                    `notificationType.${notificationType.LabelKey}` as TranslationKey
                                                                )}
                                                            </option>
                                                        ))}
                                                    </ux-select>
                                                </div>
                                            </div>
                                            {this.selectedNotificationType !== NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE && (
                                                <Fragment>
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-select
                                                                required
                                                                label={t("field.reason.label")}
                                                                data-validation-required--msg={t("validation.requiredField")}
                                                                name="productNo"
                                                                onUxChange={(event) => {
                                                                    this.selectedMachineTechnology =
                                                                        event.detail.selectedOption.attributes[
                                                                            "data-technology"
                                                                        ].value;
                                                                }}
                                                            >
                                                                <option
                                                                    value=""
                                                                    selected={this.notificationTypeName === null}
                                                                    disabled
                                                                >
                                                                    {t("select.placeholder")}
                                                                </option>
                                                                {this.notificationTypes.map((notificationType) => (
                                                                    <option
                                                                        key={String(notificationType.LabelKey)}
                                                                        value={notificationType.Id}
                                                                        selected={
                                                                            notificationType.Id === this.selectedNotificationType
                                                                        }
                                                                    >
                                                                        {/* TODO: why is Application Issues selected when this.notificationTypeName is null? */}
                                                                        {t(
                                                                            `notificationType.${notificationType.LabelKey}` as TranslationKey
                                                                        )}
                                                                    </option>
                                                                ))}
                                                            </ux-select>
                                                        </div>
                                                    </div>
                                                    {this.selectedNotificationType !== NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE && (
                                                        <div class="ux-col-12 ux-col-md-6">
                                                            <div class="ux-u-margin-bottom-20">
                                                                <ux-select
                                                                    required
                                                                    label={t("field.machine.label")}
                                                                    data-validation-required--msg={t("validation.requiredField")}
                                                                    name="application"
                                                                    onUxChange={(event) => {
                                                                        this.selectedApplication = parseInt(event.detail.value, 10);
                                                                    }}
                                                                >
                                                                    <option value="" selected disabled>
                                                                        {t("select.placeholder")}
                                                                    </option>
                                                                    {this.machines.map((machine) => (
                                                                        <option
                                                                            key={machine.productNo}
                                                                            data-technology={machine.technology}
                                                                            value={machine.productNo}
                                                                        >
                                                                            {machine.nameOnLabel} ({machine.productNo})
                                                                        </option>
                                                                    ))}
                                                                </ux-select>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Fragment>
                                            )}
                                        </div>
                                        {this.selectedNotificationType === NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE && (
                                            <Fragment>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-select
                                                                required
                                                                label={t("field.softwareProblemClassification.label")}
                                                                data-validation-required--msg={t("validation.requiredField")}
                                                                name="softwareProblemClassification"
                                                            >
                                                                <option value="" selected disabled>
                                                                    {t("select.placeholder")}
                                                                </option>
                                                                {classifications.map((classification) => (
                                                                    <option value={classification}>
                                                                        {t(
                                                                            `softwareProblemClassification.${classification}` as TranslationKey
                                                                        )}
                                                                    </option>
                                                                ))}
                                                            </ux-select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-checkbox
                                                                label={t("field.softwareInstallationProblem.label")}
                                                                name="installationProblem"
                                                            ></ux-checkbox>
                                                        </div>
                                                    </div>
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-checkbox
                                                                label={t("field.softwareLicenseIssue.label")}
                                                                name="licenseIssue"
                                                            ></ux-checkbox>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-select
                                                                required
                                                                label={t("field.softwareType.label")}
                                                                name="softwareType"
                                                                onUxChange={(event) => {
                                                                    this.selectedSoftwareType = event.detail.value;
                                                                }}
                                                            >
                                                                <option value="" selected disabled>
                                                                    {t("select.placeholder")}
                                                                </option>
                                                                {softwareTypes.map((softwareType) => (
                                                                    <option value={softwareType}>{softwareType}</option>
                                                                ))}
                                                            </ux-select>
                                                        </div>
                                                    </div>
                                                    {(this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS ||
                                                        this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS_FAB ||
                                                        this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS_BOOST) && (
                                                        <div class="ux-col-12 ux-col-md-6">
                                                            <div class="ux-u-margin-bottom-20">
                                                                <ux-select
                                                                    required
                                                                    label={t("field.application.label")}
                                                                    data-validation-required--msg={t("validation.requiredField")}
                                                                    name="application"
                                                                    onUxSelectChange={(event) => {
                                                                        this.selectedApplication = event.detail;
                                                                    }}
                                                                >
                                                                    <option value="" selected disabled>
                                                                        {t("select.placeholder")}
                                                                    </option>
                                                                    {applicationsLaser.map((application) => (
                                                                        <option value={application.id}>
                                                                            {t(
                                                                                `application.${application.labelKey}` as TranslationKey
                                                                            )}
                                                                        </option>
                                                                    ))}
                                                                </ux-select>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                {this.selectedApplication === APPLICATION_IDS.WELDING && (
                                                    <div class="ux-row">
                                                        <div class="ux-col-12 ux-col-md-6">
                                                            <div class="ux-u-margin-bottom-20">
                                                                <ux-select label={t("field.optics.label")} name="optics">
                                                                    <option value="" selected disabled>
                                                                        {t("select.placeholder")}
                                                                    </option>
                                                                    {applicationsOptics.map((application) => (
                                                                        <option value={application.id}>
                                                                            {t(
                                                                                `application.${application.labelKey}` as TranslationKey
                                                                            )}
                                                                        </option>
                                                                    ))}
                                                                </ux-select>
                                                            </div>
                                                        </div>
                                                        <div class="ux-col-12 ux-col-md-6">
                                                            <div class="ux-u-margin-bottom-20">
                                                                <ux-select
                                                                    label={t("field.applicationIssue.label")}
                                                                    name="applicationIssue"
                                                                >
                                                                    <option value="" selected disabled>
                                                                        {t("select.placeholder")}
                                                                    </option>
                                                                    {applicationsWelding.map((application) => (
                                                                        <option value={application.id}>
                                                                            {t(
                                                                                `application.${application.labelKey}` as TranslationKey
                                                                            )}
                                                                        </option>
                                                                    ))}
                                                                </ux-select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Fragment>
                                        )}
                                        {this.selectedNotificationType === NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE && (
                                            <Fragment>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-select
                                                                required
                                                                label={t("field.softwareProblemClassification.label")}
                                                                data-validation-required--msg={t("validation.requiredField")}
                                                                name="softwareProblemClassification"
                                                            >
                                                                <option value="" selected disabled>
                                                                    {t("select.placeholder")}
                                                                </option>
                                                                {classifications.map((classification) => (
                                                                    <option value={classification}>
                                                                        {t(
                                                                            `softwareProblemClassification.${classification}` as TranslationKey
                                                                        )}
                                                                    </option>
                                                                ))}
                                                            </ux-select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-checkbox
                                                                label={t("field.softwareInstallationProblem.label")}
                                                                name="installationProblem"
                                                            ></ux-checkbox>
                                                        </div>
                                                    </div>
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-checkbox
                                                                label={t("field.softwareLicenseIssue.label")}
                                                                name="licenseIssue"
                                                            ></ux-checkbox>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-select
                                                                required
                                                                label={t("field.softwareType.label")}
                                                                name="softwareType"
                                                                onUxSelectChange={(event) => {
                                                                    this.selectedSoftwareType = event.detail;
                                                                }}
                                                            >
                                                                <option value="" selected disabled>
                                                                    {t("select.placeholder")}
                                                                </option>
                                                                {softwareTypes.map((softwareType) => (
                                                                    <option value={softwareType}>{softwareType}</option>
                                                                ))}
                                                            </ux-select>
                                                        </div>
                                                    </div>
                                                    {(this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS ||
                                                        this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS_FAB ||
                                                        this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS_BOOST) && (
                                                        <div class="ux-col-12 ux-col-md-6">
                                                            <div class="ux-u-margin-bottom-20">
                                                                <ux-select
                                                                    required
                                                                    label={t("field.softwareModule.label")}
                                                                    data-validation-required--msg={t("validation.requiredField")}
                                                                    name="softwareModule"
                                                                >
                                                                    <option value="" selected disabled>
                                                                        {t("select.placeholder")}
                                                                    </option>
                                                                    {getSoftwareModulesByType(
                                                                        this.softwares,
                                                                        this.selectedSoftwareType
                                                                    ).map((softwareModule) => (
                                                                        <option value={softwareModule}>
                                                                            {t(
                                                                                `softwareModule.${softwareModule}` as TranslationKey
                                                                            )}
                                                                        </option>
                                                                    ))}
                                                                </ux-select>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Fragment>
                                        )}
                                        {showErrorNumbers && (
                                            <div class="ux-row">
                                                <div class="ux-col-12 ux-col-md-6">
                                                    <div class="ux-u-margin-bottom-20">
                                                        <label class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                                            {t("field.errorNumber.label")}
                                                        </label>
                                                        <service-calls-dynamic-input
                                                            add-text={t("field.errorNumber.addAnotherNumber")}
                                                            input-name="errorNumber1"
                                                            prefilled-values={this.errorNumbers.split(",")}
                                                            init-items={this.errorNumbers.split(",").length || 1}
                                                            min-items="1"
                                                            max-items="6"
                                                        ></service-calls-dynamic-input>
                                                        <div class={`${baseClass}__help-block`}>
                                                            <p class="ux-text-tiny">
                                                                {t("field.errorNumber.explanation", {
                                                                    example1: "0000FC00",
                                                                    example2: "1.10.58102409",
                                                                    example3: "6000"
                                                                })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div class="ux-row">
                                            <div class="ux-col-12">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-textarea-field
                                                        label={t("field.message.label")}
                                                        required
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        name="description"
                                                    ></ux-textarea-field>
                                                </div>
                                            </div>
                                        </div>

                                        {this.fileUpload && (
                                            <div class="ux-row">
                                                <div class="ux-col-12">
                                                    <ux-file-upload
                                                        max-file-size="2"
                                                        label={t("field.fileUpload.label")}
                                                        accepted-files="image/*,application/pdf,text/html"
                                                        max-files="4"
                                                    ></ux-file-upload>
                                                </div>
                                            </div>
                                        )}

                                        {this.multipleSteps && (
                                            <div class="ux-row">
                                                <div class="ux-col-12 ux-u-margin-bottom-20">
                                                    <div class="ux-text-normal ux-text-normal--light-gray">
                                                        <p>{t("validation.requiredFieldsExplanation")}</p>
                                                    </div>
                                                </div>
                                                <div class="ux-col-12">
                                                    <ux-button variant="primary" onClick={() => (this.step = 2)}>
                                                        {t("button.next")}
                                                    </ux-button>

                                                    <ux-button type="button" onClick={() => this.onCancel()}>
                                                        {t("button.cancel")}
                                                    </ux-button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {((this.multipleSteps && this.step === 2) || !this.multipleSteps) && (
                                <div class="ux-row ux-u-flex-jc-center">
                                    <div class="fields-wrapper ux-col-12 ux-col-md-10">
                                        <div>
                                            <h2>{t("headline.contactData")}</h2>
                                            <slot name="contacts-button"></slot>
                                        </div>

                                        <div class="ux-row">
                                            <div class="ux-col-12 ux-col-md-6">
                                                <ux-select label={t("field.salutation.label")} required name="salutation">
                                                    <option value="" selected={this.salutation === null} disabled>
                                                        {t("select.placeholder")}
                                                    </option>
                                                    <option value={SALUTATION.FEMALE} selected={this.salutation === "FEMALE"}>
                                                        {t("field.salutation.female")}
                                                    </option>
                                                    <option value={SALUTATION.MALE} selected={this.salutation === "MALE"}>
                                                        {t("field.salutation.male")}
                                                    </option>
                                                </ux-select>
                                            </div>
                                        </div>

                                        <div class="ux-row">
                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-input-field
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        data-validation-length--max="45"
                                                        data-validation-length--msg={t("validation.maxAllowedCharacters", {
                                                            number: 45
                                                        })}
                                                        label={t("field.firstName.label")}
                                                        name="firstName"
                                                        value={this.firstName}
                                                        required
                                                    ></ux-input-field>
                                                </div>
                                            </div>

                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-input-field
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        data-validation-length--max="45"
                                                        data-validation-length--msg={t("validation.maxAllowedCharacters", {
                                                            number: 45
                                                        })}
                                                        label={t("field.lastName.label")}
                                                        name="lastName"
                                                        value={this.lastName}
                                                        required
                                                    ></ux-input-field>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="ux-row">
                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-phone-input
                                                        label={t("field.phone.label")}
                                                        language={getLanguageFromLocale(this.locale)}
                                                        country={getCountryFromLocale(this.locale)}
                                                        data-validation-invalid--msg={t("validation.invalidPhoneNumber")}
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        name="phone"
                                                        value={this.phone}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-input-field
                                                        type="email"
                                                        required
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        data-validation-length--max="100"
                                                        data-validation-length--msg={t("validation.maxAllowedCharacters", {
                                                            number: 100
                                                        })}
                                                        data-validation-invalid--msg={t("validation.invalidEmail")}
                                                        label="E-Mail"
                                                        name="email"
                                                        value={this.email}
                                                    ></ux-input-field>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="ux-u-margin-bottom-20">
                                            <div class="ux-text-normal ux-text-normal--light-gray">
                                                <p>{t("validation.requiredFieldsExplanation")}</p>
                                            </div>
                                        </div>

                                        <div class={`${baseClass}__button-group`}>
                                            <ux-button type="submit" variant="primary">
                                                {t("button.submit")}
                                            </ux-button>

                                            {this.multipleSteps && (
                                                <ux-button type="button" onClick={() => (this.step = 1)}>
                                                    {t("button.back")}
                                                </ux-button>
                                            )}
                                            {!this.multipleSteps && (
                                                <ux-button type="button" onClick={() => this.onCancel()} formnovalidate>
                                                    {t("button.cancel")}
                                                </ux-button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </form>
                    </ux-form>
                )}
                <div style={{ display: this.formIsSubmitted ? "block" : "none" }}>
                    <slot name="connector" />
                </div>
                {!this.disableSilentAuth && (
                    <ux-u-silent-auth
                        client-id={authSettings.clientId}
                        issuer={authSettings.issuer}
                        redirect-uri={redirectUri}
                        scope={authSettings.scope}
                    ></ux-u-silent-auth>
                )}
            </Host>
        );
    }
}
